/**
 * /p/{projectSlug}/… — path-addressable project views (PO decision
 * 2026-07-14). A shared or bookmarked link selects the project (cookie) and
 * continues to the wrapped path; an unknown or invisible slug falls back to
 * All projects silently — the 404 contract never names what the caller
 * cannot see. The canonical in-app URL stays prefix-less after resolution;
 * prefix-preserving routing is a reserved follow-up.
 */

import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { LoadingState } from '@systemdox/ui';
import { projectsApi } from '@/services/projectsApi';
import { rememberProject, rememberSpace } from '@/contexts/ProjectContext';

export function ProjectPathResolver() {
  const params = useParams();
  const navigate = useNavigate();
  const projectSlug = params.projectSlug ?? '';
  const rest = params['*'] ?? '';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const project = await projectsApi.getProject(projectSlug);
        if (!cancelled) {
          // A project selection writes BOTH scope cookies — a project implies
          // its space (scope-selector invariant).
          rememberProject(project.projectId);
          rememberSpace(project.spaceId);
        }
      } catch {
        if (!cancelled) rememberProject(null); // silent fallback to All
      }
      if (!cancelled) {
        navigate(`/${rest}${window.location.search}`, { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectSlug, rest, navigate]);

  return (
    <LoadingState size="lg" className="min-h-screen bg-slate-50 dark:bg-slate-950" />
  );
}
